import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, FileText, CheckCircle } from 'lucide-react';
import { GlassButton } from '../components/ui/glass-button';

const Tuzuk: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    document.title = "Flying Slap - Tüzüğümüz";
    window.scrollTo(0, 0);
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="relative text-gray-900 overflow-hidden font-body min-h-screen bg-white">
      {/* 1. HERO SECTION */}
      <section style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: isMobile ? '100svh' : '40vh',
        padding: '120px 24px 40px 24px',
        background: '#ffffff'
      }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          style={{ textAlign: 'center', zIndex: 10, marginTop: isMobile ? -80 : 0 }}
        >
          <h1 className="font-headline" style={{
            fontWeight: 900,
            letterSpacing: '-0.04em',
            fontSize: isMobile ? '4.2rem' : 'clamp(2.5rem, 6vw, 4rem)',
            lineHeight: isMobile ? 1.05 : 1.1,
            color: '#0f2d5e'
          }}>
            {isMobile ? (
              <>
                <span style={{ display: 'block' }}>KULÜP</span>
                <span style={{ display: 'block', color: '#e65100' }}>TÜZÜĞÜMÜZ</span>
              </>
            ) : (
              <>KULÜP <span style={{ color: '#e65100' }}>TÜZÜĞÜMÜZ</span></>
            )}
          </h1>
        </motion.div>

        {isMobile && (
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
            style={{
              position: 'absolute',
              bottom: 32,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 1,
              height: 56,
              background: 'linear-gradient(to bottom, #e65100, transparent)'
            }}
          />
        )}
      </section>

      {/* 2. CONTENT SECTION */}
      <section style={{ position: 'relative', padding: isMobile ? '40px 0px 80px 0px' : '40px 24px 80px 24px', background: '#fff', zIndex: 5 }}>
        <div className="max-w-6xl mx-auto">
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '350px 1fr',
            gap: isMobile ? 80 : 48,
            alignItems: 'start'
          }}>
            {/* Sidebar / Info Card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              style={{
                background: 'rgba(15, 45, 94, 0.03)',
                border: '1px solid rgba(15, 45, 94, 0.05)',
                borderRadius: 32,
                padding: isMobile ? 40 : 32,
                position: isMobile ? 'relative' : 'sticky',
                top: 100,
                marginBottom: isMobile ? 80 : 0
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: '#0f2d5e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FileText size={20} color="#fff" />
                </div>
                <h3 style={{ fontSize: isMobile ? 22 : 18, fontWeight: 800, color: '#0f2d5e' }}>Resmi Belgeler</h3>
              </div>
              <p style={{ color: '#0f2d5e', opacity: 0.8, fontSize: isMobile ? 17 : 15, lineHeight: 1.6, marginBottom: 24 }}>
                Kulübümüz, Türkiye Hava Sporları Federasyonu ve Gençlik ve Spor Bakanlığı yönetmeliklerine uygun olarak tüzük altına alınmıştır.
              </p>

              <ul style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 0, listStyle: 'none' }}>
                {['Şeffaf Yönetim', 'Eğitim Odaklılık', 'Sportif Etik', 'Gençlik Gelişimi'].map((item, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#0f2d5e', fontSize: isMobile ? 16 : 14, fontWeight: 600 }}>
                    <CheckCircle size={16} style={{ color: '#e65100' }} />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Main Content */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              style={{
                background: '#fff',
                borderRadius: 32,
                boxShadow: '0 20px 50px rgba(0,0,0,0.05)',
                padding: isMobile ? '32px 24px' : '60px',
                border: '1px solid rgba(0,0,0,0.02)'
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                <div style={{
                  background: 'linear-gradient(135deg, #0f2d5e 0%, #1e40af 100%)',
                  borderRadius: 24,
                  padding: isMobile ? 32 : 32,
                  color: '#fff',
                  boxShadow: '0 20px 40px rgba(15, 45, 94, 0.2)'
                }}>
                  <h3 style={{ fontFamily: 'var(--font-headline)', fontWeight: 800, fontSize: isMobile ? 26 : 22, marginBottom: 8 }}>
                    KULÜP TÜZÜĞÜ
                  </h3>
                  <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    justifyContent: 'space-between',
                    gap: 20
                  }}>
                    <p style={{ opacity: 0.9, fontSize: isMobile ? 17 : 15, lineHeight: 1.6, marginBottom: isMobile ? 20 : 0, maxWidth: 450 }}>
                      Kulüp Tüzüğüne butona tıklayarak ulaşabilirsiniz.
                    </p>
                    <GlassButton
                      onClick={() => window.open('/Flying Slap Spor Kulübü Tüzüğü.pdf', '_blank')}
                      contentClassName="group"
                      style={{
                        flexShrink: 0,
                        background: 'linear-gradient(135deg, #e65100 0%, #ff9800 100%)',
                        color: '#ffffff',
                        border: 'none',
                        boxShadow: '0 10px 20px rgba(230, 81, 0, 0.3)',
                        padding: isMobile ? '16px 36px' : '14px 32px',
                        borderRadius: '999px',
                        position: 'relative',
                        top: '-12px',
                        cursor: 'pointer'
                      }}
                    >
                      KULÜP TÜZÜĞÜ <ChevronRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </GlassButton>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. FOOTER SPACE */}
      <div style={{ height: 100, background: '#fff' }} />
    </div>
  );
};

export default Tuzuk;
